import { useState } from 'react';
import { ApiError, api } from '../api';
import { Banner, Field, Pill, Spinner, useAsync } from '../components/Common';
import { formatDateTime, relativeTime } from '../format';

interface HealthPayload {
  connections: { id: number; name: string; slug: string; kind: string; enabled: number; last_ok_at: string | null; last_error: string | null }[];
  cacheEntries: number;
  runsToday: number;
  failuresToday: number;
  schedules: { id: number; name: string; cron: string; enabled: number; last_run_at: string | null; last_status: string | null; next_run_at: string | null; report_name: string }[];
}

export function Health() {
  const { data, error, loading, reload } = useAsync(() => api.get<HealthPayload>('/api/admin/health'));
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const purge = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ cleared: number }>('/api/admin/purge-cache');
      setMessage(`Cleared ${res.cleared} cached upstream response(s). The next run will re-query the source systems.`);
      reload();
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <Spinner label="Checking system health…" />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!data) return null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>System health</h1>
          <p>Whether the upstream systems are answering, and what the scheduler has been doing.</p>
        </div>
        <div className="page-actions">
          <button onClick={reload}>Refresh</button>
          <button onClick={purge} disabled={busy} title="Discard cached upstream responses">
            Clear data cache
          </button>
        </div>
      </div>

      {message && <Banner tone="info">{message}</Banner>}

      <div className="kpi-row" style={{ marginBottom: 18 }}>
        <div className="kpi">
          <div className="label">Runs today</div>
          <div className="value">{data.runsToday}</div>
        </div>
        <div className="kpi">
          <div className="label">Failed or partial today</div>
          <div className="value">{data.failuresToday}</div>
          {data.failuresToday > 0 && <div className="sub">Check the entry points below</div>}
        </div>
        <div className="kpi">
          <div className="label">Cached upstream responses</div>
          <div className="value">{data.cacheEntries}</div>
        </div>
        <div className="kpi">
          <div className="label">Active schedules</div>
          <div className="value">{data.schedules.filter((s) => s.enabled).length}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3>Entry points</h3>
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Entry point</th>
                <th>Type</th>
                <th>Last success</th>
                <th>State</th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {data.connections.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td className="mono">{c.kind}</td>
                  <td>{relativeTime(c.last_ok_at)}</td>
                  <td>{c.enabled ? <Pill tone="ok">enabled</Pill> : <Pill tone="off">disabled</Pill>}</td>
                  <td style={{ color: 'var(--critical)', maxWidth: 380 }}>{c.last_error ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h3>Scheduler</h3>
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Schedule</th>
                <th>Report</th>
                <th className="mono">Cron</th>
                <th>Last run</th>
                <th>Result</th>
                <th>Next</th>
              </tr>
            </thead>
            <tbody>
              {data.schedules.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.report_name}</td>
                  <td className="mono">{s.cron}</td>
                  <td>{relativeTime(s.last_run_at)}</td>
                  <td>
                    {s.last_status ? (
                      s.last_status.startsWith('ok') ? (
                        <Pill tone="ok">ok</Pill>
                      ) : (
                        <Pill tone="bad">{s.last_status}</Pill>
                      )
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{s.enabled ? formatDateTime(s.next_run_at) : 'paused'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!data.schedules.length && <p className="note">No schedules configured.</p>}
      </div>

      <Definitions />
    </>
  );
}

/**
 * Definition import / export.
 *
 * The immediate use: the seeded Bullhorn requests were written against inferred
 * argument names. Once the real tool schemas are known from the live server, the
 * corrected definitions can be pasted straight in — no credential is involved,
 * because entry points are referenced by slug and URLs and tokens are never part
 * of a bundle.
 */
function Definitions() {
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ applied: boolean; summary: string; results: any[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const exportNow = async () => {
    setBusy(true);
    setError(null);
    try {
      const bundle = await api.get<any>('/api/admin/definitions');
      const json = JSON.stringify(bundle, null, 2);
      setText(json);
      // Also offer it as a file, since the usual next step is sending it somewhere.
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `scg-reporting-definitions-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const send = async (apply: boolean) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const bundle = JSON.parse(text);
      setResult(await api.post('/api/admin/definitions', { ...bundle, apply }));
    } catch (err) {
      setError(err instanceof SyntaxError ? `That is not valid JSON: ${err.message}` : err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Definitions — export and import</h3>
      <p className="note" style={{ marginTop: 0, marginBottom: 12 }}>
        Moves request and report definitions between installations as plain JSON. Entry points are referenced by slug —
        URLs and credentials are never included, so a bundle is safe to send by email. Importing never touches the rows in
        a managed list.
      </p>

      {error && <Banner tone="error">{error}</Banner>}
      {result && (
        <Banner tone={result.applied ? 'good' : 'info'} title={result.summary}>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
            {result.results.map((r, i) => (
              <li key={i}>
                {r.kind} <strong>{r.slug}</strong> — {r.action}
                {r.reason ? ` (${r.reason})` : ''}
              </li>
            ))}
          </ul>
        </Banner>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={exportNow} disabled={busy}>
          Export current definitions
        </button>
        <button onClick={() => send(false)} disabled={busy || !text.trim()}>
          Check import (dry run)
        </button>
        <button className="primary" onClick={() => send(true)} disabled={busy || !text.trim()}>
          Apply import
        </button>
      </div>

      <Field label="Definition bundle (JSON)" help="Dry-run first — it reports exactly what would be created or updated before anything changes.">
        <textarea rows={10} value={text} spellCheck={false} onChange={(e) => setText(e.target.value)} placeholder="Paste a definition bundle here…" />
      </Field>
    </div>
  );
}
